<template>
  <Dialog
    v-model:visible="visible"
    modal
    :header="$t('auth.register.title')"
    :style="{ width: '450px' }"
    :closable="false"
    :close-on-escape="false"
  >
    <form class="space-y-4" @submit.prevent="handleRegister">
      <div class="field">
        <label for="username" class="block text-sm font-medium mb-2">{{
          $t('auth.login.usernameLabel')
        }}</label>
        <InputText
          id="username"
          v-model="form.username"
          type="text"
          :placeholder="$t('auth.login.usernamePlaceholder')"
          :class="{ 'p-invalid': errors.username }"
          required
        />
        <small v-if="errors.username" class="p-error">{{
          errors.username
        }}</small>
      </div>

      <div class="field">
        <label for="email" class="block text-sm font-medium mb-2">{{
          $t('auth.register.emailLabel')
        }}</label>
        <InputText
          id="email"
          v-model="form.email"
          type="email"
          :placeholder="$t('auth.register.emailPlaceholder')"
          :class="{ 'p-invalid': errors.email }"
          required
        />
        <small v-if="errors.email" class="p-error">{{ errors.email }}</small>
      </div>

      <div class="field">
        <label for="password" class="block text-sm font-medium mb-2">{{
          $t('auth.login.passwordLabel')
        }}</label>
        <Password
          id="password"
          v-model="form.password"
          :placeholder="$t('auth.login.passwordPlaceholder')"
          :class="{ 'p-invalid': errors.password }"
          :feedback="true"
          toggle-mask
          required
        />
        <small v-if="errors.password" class="p-error">{{
          errors.password
        }}</small>
      </div>

      <div class="field">
        <label for="confirmPassword" class="block text-sm font-medium mb-2">{{
          $t('auth.login.confirmPasswordLabel')
        }}</label>
        <Password
          id="confirmPassword"
          v-model="form.confirmPassword"
          :placeholder="$t('auth.login.confirmPasswordPlaceholder')"
          :class="{ 'p-invalid': errors.confirmPassword }"
          :feedback="false"
          toggle-mask
          required
        />
        <small v-if="errors.confirmPassword" class="p-error">{{
          errors.confirmPassword
        }}</small>
      </div>

      <div v-if="authStore.error" class="p-error text-sm">
        {{ authStore.error }}
      </div>
    </form>

    <template #footer>
      <div class="flex justify-end gap-2">
        <Button
          :label="$t('g.cancel')"
          icon="pi pi-times"
          class="p-button-text"
          @click="handleCancel"
        />
        <Button
          :label="$t('auth.register.title')"
          icon="pi pi-check"
          :loading="authStore.isLoading"
          @click="handleRegister"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Password from 'primevue/password'
import { computed, reactive, watch } from 'vue'

import { useAuthStore } from '@/stores/authStore'

interface Props {
  visible: boolean
}

interface Emits {
  (e: 'update:visible', value: boolean): void
  (e: 'success'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const authStore = useAuthStore()

const form = reactive({
  username: '',
  email: '',
  password: '',
  confirmPassword: ''
})

const errors = reactive({
  username: '',
  email: '',
  password: '',
  confirmPassword: ''
})

const validateForm = () => {
  // Reset errors
  Object.keys(errors).forEach((key) => {
    errors[key as keyof typeof errors] = ''
  })

  let isValid = true

  // Username validation
  if (!form.username.trim()) {
    errors.username = 'Username is required'
    isValid = false
  } else if (form.username.length < 3) {
    errors.username = 'Username must be at least 3 characters'
    isValid = false
  }

  // Email validation
  if (!form.email.trim()) {
    errors.email = 'Email is required'
    isValid = false
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
    errors.email = 'Please enter a valid email address'
    isValid = false
  }

  // Password validation
  if (!form.password) {
    errors.password = 'Password is required'
    isValid = false
  } else if (form.password.length < 6) {
    errors.password = 'Password must be at least 6 characters'
    isValid = false
  }

  // Confirm password validation
  if (!form.confirmPassword) {
    errors.confirmPassword = 'Please confirm your password'
    isValid = false
  } else if (form.password !== form.confirmPassword) {
    errors.confirmPassword = 'Passwords do not match'
    isValid = false
  }

  return isValid
}

const handleRegister = async () => {
  if (!validateForm()) return

  const success = await authStore.register({
    username: form.username,
    email: form.email,
    password: form.password
  })

  if (success) {
    emit('success')
    visible.value = false
    resetForm()
  }
}

const handleCancel = () => {
  visible.value = false
  resetForm()
}

const resetForm = () => {
  form.username = ''
  form.email = ''
  form.password = ''
  form.confirmPassword = ''
  Object.keys(errors).forEach((key) => {
    errors[key as keyof typeof errors] = ''
  })
}

const visible = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value)
})

// Reset form when dialog opens
watch(visible, (newValue) => {
  if (newValue) {
    resetForm()
  }
})
</script>

<style scoped>
.field {
  margin-bottom: 1rem;
}

.space-y-4 > * + * {
  margin-top: 1rem;
}
</style>
